<template>
  <component
    :is="props.tag"
    class="cursor-pointer"
    :role="maskingRole"
    :tabindex="maskingTabindex"
    :aria-label="maskingAriaLabelOrUndefined"
    @click="navigateToAnotherPageIfMaskingIsEnabled"
    @keydown.enter="navigateToAnotherPageIfMaskingIsEnabled"
    @keydown.space.prevent="navigateToAnotherPageIfMaskingIsEnabled"
  >
    <!-- Masking enabled -->
    <template v-if="!props.maskingDisabled">
      <slot />
    </template>
    <component
      :is="unmaskedLinkIs"
      v-if="props.maskingDisabled && !props.newTab"
      :class="props.maskingDisabledCss"
      target="_self"
      :aria-label="props.maskingAriaLabel"
      v-bind="{
        ...(props.to ? { to: props.to } : {}),
        ...(!props.to && props.href ? { href: props.href } : {}),
      }"
    >
      <slot />
    </component>
    <component
      :is="unmaskedLinkIs"
      v-if="props.maskingDisabled && props.newTab"
      :class="props.maskingDisabledCss"
      target="_blank"
      :aria-label="props.maskingAriaLabel"
      v-bind="{
        ...(props.to ? { to: props.to } : {}),
        ...(!props.to && props.href ? { href: props.href } : {}),
      }"
    >
      <slot />
    </component>
  </component>
</template>

<script setup>
const props = defineProps({
  to: {
    type: String,
    default: null
  },
  href: {
    type: String,
    default: null
  },
  external: {
    type: Boolean,
    default: false
  },
  newTab: {
    type: Boolean,
    default: true
  },
  tag: {
    type: String,
    default: 'div'
  },
  maskingDisabled: {
    type: Boolean,
    default: false
  },
  maskingDisabledCss: {
    type: String,
    default: null
  },
  maskingAriaLabel: {
    type: [String, Array, Number],
    default: ''
  },
  /**
   * When true (default), masked roots get role="link" + tabindex for keyboard a11y.
   * Set false for large wrappers (e.g. object cards) that nest other controls and
   * should not become Lighthouse touch targets.
   */
  linkSemantics: {
    type: Boolean,
    default: true
  }
})

const attrs = useAttrs()

const isNativeInteractive = computed(() => props.tag === 'button' || props.tag === 'a')

/** When masking: act as a link unless the host tag is already a native control. */
const maskingRole = computed(() => {
  if (props.maskingDisabled || !props.linkSemantics) {
    return undefined
  }
  if (isNativeInteractive.value) {
    return undefined
  }
  return 'link'
})

const maskingTabindex = computed(() => {
  if (props.maskingDisabled || !props.linkSemantics || isNativeInteractive.value) {
    return undefined
  }
  return 0
})

const maskingAriaLabelOrUndefined = computed(() => {
  if (props.maskingAriaLabel) {
    return props.maskingAriaLabel
  }
  const attrLabel = attrs['aria-label']
  return typeof attrLabel === 'string' || typeof attrLabel === 'number' ? attrLabel : undefined
})

const unmaskedLinkIs = computed(() => props.to ? resolveComponent('NuxtLink') : (props.href ? 'a' : props.tag))

async function navigateToAnotherPageIfMaskingIsEnabled () {
  if (props.maskingDisabled) {
    return
  }

  // Nuxt link (prefetching etc.)
  if (props.to) {
    const openOption = props.newTab ? { open: { target: '_blank' } } : {}
    await navigateTo(
      props.to,
      {
        redirectCode: 302,
        external: props.external || props.newTab,
        ...openOption
      }
    )
    return
  }

  // Normal link (No prefetching)
  if (!props.to && props.href) {
    window.open(props.href, props.newTab ? '_blank' : '_self')
  }
}
</script>
